<template>
  <header class="app-header marketing">
    <div class="header-backdrop"></div>

    <v-container class="header-container">
      <!-- Brand -->
      <router-link to="/" class="header-brand" aria-label="Go to homepage - StructEngine">
        <logo variant="full" theme="auto" size="lg" />
        <v-chip size="x-small" color="primary" variant="outlined" class="beta-badge">Beta</v-chip>
      </router-link>

      <!-- Desktop Navigation -->
      <nav class="desktop-nav" aria-label="Main navigation">
        <!-- Calculators Dropdown -->
        <div class="nav-item dropdown-container">
          <se-button
            id="calculators-trigger"
            variant="text"
            class="dropdown-trigger"
            :aria-expanded="showCalculators"
            aria-controls="calculators-dropdown"
            @mouseenter="showCalculators = true"
            @mouseleave="startHideTimer"
            @click="showCalculators = !showCalculators"
            @keydown.escape="showCalculators = false"
          >
            <span>Calculators</span>
            <v-icon size="16" :class="{ 'rotate': showCalculators }">mdi-chevron-down</v-icon>
          </se-button>

          <div
            v-show="showCalculators"
            id="calculators-dropdown"
            class="dropdown-menu calculators-menu"
            role="group"
            aria-labelledby="calculators-trigger"
            @mouseenter="clearHideTimer"
            @mouseleave="hideCalculators"
          >
            <div class="mega-wrap">

              <!-- LEFT PANEL: categories only -->
              <div class="mega-left">
                <se-button
                  v-for="group in calculatorGroups"
                  :key="group.domain"
                  type="button"
                  variant="text"
                  style-set="set8"
                  class="cat-row"
                  :class="{ active: activeGroup === group.domain }"
                  @mouseenter="scheduleActivate(group.domain)"
                  @focus="scheduleActivate(group.domain)"
                  @mouseleave="cancelActivate"
                >
                  <v-icon :icon="group.icon" size="16" class="cat-row-icon" />
                  <span class="cat-row-label">{{ group.title }}</span>
                  <span class="cat-row-badge">{{ domainBadge[group.domain] }}</span>
                  <v-icon icon="mdi-chevron-right" size="13" class="cat-row-arrow" />
                </se-button>

                <div class="left-footer">
                  <router-link to="/calculators" class="browse-all" @click="closeMenus">
                    Browse all calculators
                    <v-icon icon="mdi-arrow-right" size="13" />
                  </router-link>
                </div>
              </div>

              <!-- RIGHT PANEL: sub-items for active category -->
              <div class="mega-right">
                <transition name="panel-slide" mode="out-in">
                  <div v-if="activeGroupData" :key="activeGroupData.domain" class="right-inner">

                    <div class="right-header">
                      <router-link :to="activeGroupData.route" class="right-header-link" @click="closeMenus">
                        {{ activeGroupData.title }}
                        <v-icon icon="mdi-arrow-right" size="12" class="header-arrow" />
                      </router-link>
                    </div>

                    <div class="right-items">
                      <router-link
                        v-for="calc in activeGroupData.calculators"
                        :key="calc.slug"
                        :to="calc.route ?? `/calculators/${activeGroupData.domain}`"
                        class="right-item"
                        @click="closeMenus"
                      >
                        <div class="right-item-icon">
                          <s-e-icon
                            v-if="seIconMap[calc.slug]"
                            :name="seIconMap[calc.slug]"
                            :size="18"
                          />
                          <v-icon
                            v-else
                            :icon="mdiOverrideMap[calc.slug] || mdiIconMap[calc.slug.replace(/-/g, '_')] || 'mdi-calculator-variant'"
                            size="18"
                          />
                        </div>
                        <div class="right-item-body">
                          <div class="right-item-top">
                            <span class="right-item-name">{{ calc.name }}</span>
                            <v-chip v-if="calc.stage === 'beta'" size="x-small" color="primary" variant="outlined" class="beta-chip">beta</v-chip>
                          </div>
                          <span class="right-item-desc">{{ calc.shortDescription }}</span>
                        </div>
                      </router-link>
                    </div>

                  </div>
                </transition>
              </div>

            </div>
          </div>
        </div>

        <div class="nav-item">
          <se-button variant="text" @click="openDocumentation">
            Documentation
          </se-button>
        </div>

        <div class="nav-item">
          <se-button variant="text" to="/blog" @click="closeMenus">
            Blog
          </se-button>
        </div>

        <div class="nav-item">
          <se-button variant="text" to="/about" @click="closeMenus">
            About
          </se-button>
        </div>
      </nav>

      <!-- Auth/Actions Section -->
      <div class="auth-section">
        <!-- Theme Toggle (Standard Size) -->
        <theme-toggle />

        <template v-if="!isAuthenticated">
          <se-button variant="text" class="login-btn" @click="navigateTo('/login')">
            Login
          </se-button>
          <se-button variant="primary" style-set="set5" class="cta-btn" @click="navigateTo('/login')">
            Start Designing
          </se-button>
        </template>

        <template v-else>
          <div class="user-menu-container">
            <se-button
              variant="text"
              class="user-menu-trigger"
              :aria-expanded="showUserMenu"
              aria-controls="user-account-menu"
              @click="showUserMenu = !showUserMenu"
            >
              <div class="user-avatar">
                <img
                  v-if="userAvatarUrl"
                  :src="userAvatarUrl"
                  :alt="userName"
                  class="avatar-image"
                  @error="handleAvatarError"
                />
                <span v-else>{{ userInitials }}</span>
              </div>
              <v-icon size="14" :class="{ 'rotate': showUserMenu }">mdi-chevron-down</v-icon>
            </se-button>

            <div v-show="showUserMenu" id="user-account-menu" class="dropdown-menu user-menu">
              <div class="user-info">
                <div class="user-name">{{ userName }}</div>
                <div class="user-email">{{ userEmail }}</div>
                <div class="user-status">
                  Beta
                </div>
              </div>

              <div class="menu-divider"></div>

              <div class="user-menu-items">
                <se-button variant="text" class="menu-item" to="/dashboard" @click="closeMenus">
                  <v-icon size="16">mdi-view-dashboard</v-icon>
                  <span>Dashboard</span>
                </se-button>
                <se-button variant="text" class="menu-item" to="/history" @click="closeMenus">
                  <v-icon size="16">mdi-history</v-icon>
                  <span>Calculation History</span>
                </se-button>
                <se-button variant="text" class="menu-item" to="/settings" @click="closeMenus">
                  <v-icon size="16">mdi-cog</v-icon>
                  <span>Settings</span>
                </se-button>

                <div class="menu-divider"></div>

                <se-button type="button" variant="text" class="menu-item logout" @click="logout">
                  <v-icon size="16" color="error">mdi-logout</v-icon>
                  <span>Logout</span>
                </se-button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Mobile Actions (Theme Toggle + Hamburger) -->
      <div class="mobile-actions">
        <theme-toggle />
        <se-button
          variant="text"
          class="mobile-menu-btn"
          aria-label="Open navigation menu"
          :aria-expanded="showMobileMenu"
          aria-controls="mobile-navigation"
          @click="showMobileMenu = true"
        >
          <div class="hamburger">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </se-button>
      </div>
    </v-container>

    <!-- Mobile Menu Overlay -->
    <div v-show="showMobileMenu" class="mobile-overlay" @click.self="showMobileMenu = false">
      <nav
        id="mobile-navigation"
        class="mobile-menu"
        aria-label="Mobile navigation"
      >
        <div class="mobile-header">
          <div class="brand-text mobile-brand">StructEngine</div>

          <se-button
            variant="text"
            class="close-btn"
            aria-label="Close navigation menu"
            @click="showMobileMenu = false"
          >
            <v-icon size="24">mdi-close</v-icon>
          </se-button>
        </div>

        <div class="mobile-content">
          <div class="mobile-section">
            <div class="section-title">
              <v-icon size="20" color="primary">mdi-calculator</v-icon>
              <span>Calculators</span>
            </div>

            <div class="mobile-items">
              <router-link
                v-for="domain in categoryDomains"
                :key="domain"
                :to="`/calculators/${domain}`"
                class="mobile-item"
                @click="closeMenus"
              >
                <span>{{ domain.charAt(0).toUpperCase() + domain.slice(1) }}</span>
                <v-icon size="14" icon="mdi-arrow-right" />
              </router-link>
            </div>
          </div>

          <div class="mobile-section">
            <se-button type="button" variant="text" class="mobile-item" @click="openDocumentation">
              <span>Documentation</span>
            </se-button>

            <router-link to="/blog" class="mobile-item" @click="closeMenus">
              <span>Blog</span>
            </router-link>

            <router-link to="/about" class="mobile-item" @click="closeMenus">
              <span>About</span>
            </router-link>
          </div>

          <div class="mobile-auth">
            <template v-if="!isAuthenticated">
              <se-button variant="secondary" class="mobile-auth-btn login" @click="navigateTo('/login')">
                Login
              </se-button>
              <se-button variant="primary" class="mobile-auth-btn cta" @click="navigateTo('/login')">
                Start Designing
              </se-button>
            </template>

            <template v-else>
              <div class="mobile-user-info">
                <div class="user-avatar large">
                  <span>{{ userInitials }}</span>
                </div>
                <div class="user-details">
                  <div class="user-name">{{ userName }}</div>
                  <div class="user-email">{{ userEmail }}</div>
                </div>
              </div>

              <div class="mobile-user-actions">
                <se-button variant="text" class="mobile-item" to="/dashboard" @click="closeMenus">
                  <v-icon size="18">mdi-view-dashboard</v-icon>
                  <span>Dashboard</span>
                </se-button>
                <se-button type="button" variant="text" class="mobile-item logout" @click="logout">
                  <v-icon size="18" color="error">mdi-logout</v-icon>
                  <span>Logout</span>
                </se-button>
              </div>
            </template>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import SeButton from '@/components/SEButton.vue'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import Logo from '@/components/common/Logo.vue'
import SEIcon from '@/components/ui/SEIcon.vue'
import { useAuthStore } from '@/stores/auth'
import { calculatorsMeta, domainCategoryMeta, calculatorDomainCopy, type CalculatorDomain } from '@/data/calculators'
import { calculators as calculatorDirectory } from '@/data/calculatorDirectory'

// mdi icon fallback: directory id uses hyphens, slug uses underscores
const mdiIconMap = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}
  calculatorDirectory.forEach(c => { map[c.id.replace(/-/g, '_')] = c.icon })
  return map
})

// Explicit overrides for slugs that don't match normalized directory IDs
const mdiOverrideMap: Record<string, string> = {
  'is-code-lookup': 'mdi-table-search',
  'load_combination_generator': 'mdi-sigma',
}

const seIconMap: Record<string, string> = {
  conc_beam_design: 'concrete-beam',
  column_design: 'rc-column',
  slab_design: 'slab',
  crack_width: 'crack-width',
  footing_design: 'footing',
  retaining_wall: 'retaining-wall',
  base_plate: 'base-plate',
  steel_column: 'steel-column',
  purlin_design: 'purlin',
  gantry_girder: 'gantry',
}

const domainBadge: Record<string, string> = {
  steel: 'IS 800',
  concrete: 'IS 456',
  specialized: 'IS 800 / IS 456 / IS 1893'
}

const router = useRouter()
const authStore = useAuthStore()

// State
const showCalculators = ref(false)
const showUserMenu = ref(false)
const showMobileMenu = ref(false)
const activeGroup = ref<string>('steel')
let hideTimer: number | null = null
let activateTimer: number | null = null

// Auth state from store
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
const userName = computed(() => user.value?.name || user.value?.email?.split('@')[0] || 'User')
const userEmail = computed(() => user.value?.email || '')
const avatarFailed = ref(false)

// Google profile picture or null (for fallback to initials)
const userAvatarUrl = computed(() => {
  if (avatarFailed.value) return null
  return user.value?.avatar_url || null
})

const handleAvatarError = () => {
  avatarFailed.value = true
}

const userInitials = computed(() => {
  return userName.value
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const categoryDomains: CalculatorDomain[] = ['steel', 'concrete', 'specialized']

const calculatorGroups = computed(() =>
  categoryDomains.map(domain => ({
    domain,
    title: calculatorDomainCopy[domain].title,
    icon: domainCategoryMeta[domain].icon,
    route: `/calculators/${domain}`,
    calculators: calculatorsMeta.filter(
      c => c.domain === domain && (c.stage === 'live' || c.stage === 'beta')
    )
  }))
)

const activeGroupData = computed(() =>
  calculatorGroups.value.find(g => g.domain === activeGroup.value) ?? null
)

const scheduleActivate = (domain: string) => {
  if (activateTimer) clearTimeout(activateTimer)
  activateTimer = window.setTimeout(() => {
    activeGroup.value = domain
  }, 150)
}

const cancelActivate = () => {
  if (activateTimer) {
    clearTimeout(activateTimer)
    activateTimer = null
  }
}

// Methods
const closeMenus = () => {
  showMobileMenu.value = false
  showCalculators.value = false
  showUserMenu.value = false
}

const navigateTo = (path: string) => {
  router.push(path)
  closeMenus()
}

// Optimized dropdown interactions
const startHideTimer = () => {
  hideTimer = window.setTimeout(() => {
    showCalculators.value = false
  }, 200)
}

const clearHideTimer = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

const hideCalculators = () => {
  clearHideTimer()
  showCalculators.value = false
}

const logout = () => {
  authStore.logout()
  navigateTo('/')
}

// Open documentation directly (bypasses Vue router to reach backend static files)
const openDocumentation = () => {
  showMobileMenu.value = false
  window.location.href = '/documentation'
}

// Optimized outside click handler
const handleOutsideClick = (event: Event) => {
  const target = event.target as Element

  if (target.closest('.dropdown-container') || target.closest('.user-menu-container')) {
    return
  }

  if (showCalculators.value || showUserMenu.value) {
    showCalculators.value = false
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
  if (hideTimer) clearTimeout(hideTimer)
  if (activateTimer) clearTimeout(activateTimer)
})
</script>

<style scoped>
/* Header */
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  z-index: var(--z-sticky);
  border-bottom: 1px solid var(--border-subtle);
}

.header-backdrop {
  position: absolute;
  inset: 0;
  background: var(--bg-surface);
  opacity: 1;
}

.header-container {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Brand Styling */
.header-brand {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  text-decoration: none;
  /* Constrain clickable area within navbar */
  max-height: 72px;
  overflow: hidden;
  color: var(--brand-color);
}

.header-brand .beta-badge {
  font-size: var(--font-size-2xs) !important;
  height: 20px !important;
  padding: 0 8px !important;
}

.header-brand:hover {
  color: var(--accent);
}

/* Desktop Navigation */
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-item {
  position: relative;
}

.nav-item :deep(.se-btn) {
  font-size: var(--font-size-md);
  font-weight: 500;
  --btn-transition: transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease;
}

.nav-item :deep(.se-btn.se-set5.se-text) {
  text-transform: none;
  color: var(--accent);
}

.nav-item :deep(.se-btn:hover) {
  color: var(--accent);
  background: transparent;
  box-shadow: var(--shadow-inset-tab);
}

.dropdown-trigger .v-icon {
  margin-left: 8px;
  transition: transform 0.2s ease;
}

.dropdown-trigger .v-icon.rotate {
  transform: rotate(180deg);
}

/* Auth Section */
.auth-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-section .login-btn {
  font-size: var(--font-size-md);
  font-weight: 500;
  text-transform: none;
  color: var(--text-body);
  --btn-transition: transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease;
}

.auth-section .login-btn:hover {
  color: var(--text-primary);
}

/* Dropdown Menus */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  animation: dropdown-appear 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes dropdown-appear {
  from { opacity: 0; transform: translateY(-10px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ─── Calculators mega menu ─────────────────────── */
.calculators-menu {
  left: -20px;
  padding: 0;
  overflow: hidden;
  width: auto;
  box-shadow: var(--shadow-hover);
}

.mega-wrap {
  display: flex;
  min-height: 280px;
}

/* ── Left panel ─────────────────────────────────── */
.mega-left {
  width: 300px;
  flex-shrink: 0;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-subtle);
}

.mega-left .cat-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: var(--btn-height-primary);
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  border: 0;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  text-transform: none;
  transition: background 0.12s;
}

/* left accent bar */
.cat-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 0;
  background: var(--accent);
  border-radius: 0 var(--radius-xs) var(--radius-xs) 0;
  transition: height 0.15s ease;
}

.mega-left .cat-row:hover,
.mega-left .cat-row.active {
  background: rgba(var(--accent-rgb), 0.10);
}

.cat-row.active::before,
.cat-row:hover::before {
  height: 60%;
}

.cat-row-icon {
  color: var(--text-secondary);
  flex-shrink: 0;
  transition: none;
}

.cat-row.active .cat-row-icon {
  color: var(--accent);
}

.cat-row-label {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text-body);
  line-height: 1.4;
  transition: none;
}

.cat-row.active .cat-row-label {
  color: var(--text-primary);
}

.cat-row-badge {
  font-size: var(--font-size-2xs);
  font-weight: 600;
  color: var(--text-secondary);
  opacity: 0.6;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}

.cat-row.active .cat-row-badge {
  opacity: 1;
  color: var(--accent);
}

.cat-row-arrow {
  color: var(--text-secondary);
  opacity: 0.35;
  flex-shrink: 0;
  transition: opacity 0.12s, transform 0.12s;
}

.cat-row.active .cat-row-arrow {
  opacity: 1;
  color: var(--accent);
  transform: translateX(2px);
}

.left-footer {
  margin-top: auto;
  padding: 20px 24px 8px;
  border-top: 1px solid var(--border-subtle);
}

.browse-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-caption);
  font-weight: 500;
  color: var(--accent);
  text-decoration: none;
  transition: gap 0.15s;
}

.browse-all:hover {
  gap: 8px;
}

/* ── Right panel ─────────────────────────────────── */
.mega-right {
  width: 360px;
  flex-shrink: 0;
  padding: 24px 0;
  position: relative;
  overflow: hidden;
}

.right-inner {
  padding: 0 28px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* right panel enter/leave animation */
.panel-slide-enter-active {
  transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}
.panel-slide-leave-active {
  transition: opacity 0.1s ease-in;
}
.panel-slide-enter-from {
  opacity: 0;
  transform: translateX(8px);
}
.panel-slide-leave-to {
  opacity: 0;
}

.right-header {
  margin-bottom: 16px;
  padding-bottom: 0;
  border-bottom: none;
}

.right-header-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-micro);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary);
  text-decoration: none;
  transition: none;
}

.right-header-link:hover {
  color: var(--accent);
}

.header-arrow {
  opacity: 0;
  transition: opacity 0.15s, transform 0.15s;
}

.right-header-link:hover .header-arrow {
  opacity: 1;
  transform: translateX(2px);
}

.right-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.right-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px;
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: background 0.12s;
  border-left: 2px solid transparent;
}

.right-item:hover {
  background: rgba(var(--accent-rgb), 0.08);
  border-left-color: rgba(var(--accent-rgb), 0.4);
}

.right-item-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--accent-rgb), 0.08);
  border-radius: var(--radius-control);
  color: var(--accent);
  margin-top: 4px;
}

.right-item-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.right-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.right-item-name {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text-body);
  line-height: 1.3;
  transition: none;
}

.right-item:hover .right-item-name {
  color: var(--text-primary);
}

.right-item-desc {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  line-height: 1.4;
  opacity: 0.75;
}

.right-item:hover .right-item-desc {
  opacity: 0.9;
}

.beta-chip {
  font-size: var(--font-size-2xs) !important;
  height: 16px !important;
  padding: 0 4px !important;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 12px 16px;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  border-radius: var(--radius-control);
  transition: background 0.2s ease, transform 0.2s ease;
  text-decoration: none;
  text-align: left;
  text-transform: none;
}

.menu-item:hover {
  color: var(--text-primary);
  background: rgba(var(--accent-rgb), 0.08);
  transform: translateX(2px);
}

/* User Menu */
.user-menu-container {
  position: relative;
}

.user-menu-trigger {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding: 4px !important;
  border-radius: var(--radius-md) !important;
}

.user-menu-trigger:hover {
  background: color-mix(in srgb, var(--text-primary) 10%, transparent) !important;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: var(--accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bg-canvas);  /* Design token: dark in dark mode, light in light mode */
  font-size: var(--font-size-caption);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  overflow: hidden;
}

.user-avatar .avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-avatar.large {
  width: 48px;
  height: 48px;
  font-size: var(--font-size-md);
}

.user-menu {
  right: 0;
  width: 280px;
  padding: 16px;
}

.user-info {
  margin-bottom: 16px;
}

.user-name {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.user-email {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.user-status {
  display: inline-block;
  padding: 4px 8px;
  background: color-mix(in srgb, var(--text-primary) 10%, transparent);
  border-radius: var(--radius-lg);
  font-size: var(--font-size-xs);
  color: var(--text-body);
}

.menu-divider {
  height: 1px;
  background: var(--border-subtle);
  margin: 12px 0;
}

.user-menu-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-item.logout {
  color: var(--state-error);
}

/* Mobile Menu Button */
.mobile-menu-btn {
  display: none;
  padding: 8px !important;
}

.mobile-menu-btn:hover {
  background: color-mix(in srgb, var(--text-primary) 10%, transparent) !important;
}

.hamburger {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hamburger span {
  width: 20px;
  height: 2px;
  background: color-mix(in srgb, var(--text-inverse) 80%, transparent);
  border-radius: var(--radius-xs);
  transition: all 0.2s ease;
}

/* Mobile Menu Overlay */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-scrim-strong);
  backdrop-filter: blur(10px);
  z-index: var(--z-popover);
  animation: fade-in 0.2s ease;
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.mobile-menu {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 400px;
  height: 100vh;
  background: var(--surface-inverse);
  animation: slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
}

@keyframes slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid color-mix(in srgb, var(--text-inverse) 10%, transparent);
}

.close-btn {
  color: color-mix(in srgb, var(--text-inverse) 80%, transparent) !important;
  padding: 8px !important;
  border-radius: var(--radius-md) !important;
}

.mobile-content {
  padding: 24px;
}

.mobile-section {
  margin-bottom: 32px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(var(--accent-rgb), 0.2);
}

.mobile-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 12px 16px;
  color: color-mix(in srgb, var(--text-inverse) 80%, transparent);
  font-size: var(--font-size-body);
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: background 0.2s ease, transform 0.2s ease;
  text-decoration: none;
  text-align: left;
  text-transform: none;
}

.mobile-item:hover {
  color: var(--text-inverse);
  background: rgba(var(--accent-rgb), 0.1);
}

.mobile-item.active {
  color: var(--text-primary);
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  box-shadow: var(--shadow-inset-accent);
}

.mobile-item.logout {
  color: var(--state-error);
}

.mobile-brand {
  color: var(--brand-color);
}

.mobile-auth {
  border-top: 1px solid color-mix(in srgb, var(--text-inverse) 10%, transparent);
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-auth-btn {
  padding: 12px 24px !important;
  border-radius: var(--radius-lg) !important;
  font-size: var(--font-size-body) !important;
  font-weight: 500 !important;
  width: 100% !important;
  justify-content: center !important;
}

.mobile-auth-btn.login {
  background: color-mix(in srgb, var(--text-inverse) 10%, transparent) !important;
  color: color-mix(in srgb, var(--text-inverse) 90%, transparent) !important;
}

.mobile-auth-btn.cta {
  background: var(--accent) !important;
  color: var(--text-inverse) !important;
}

.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: color-mix(in srgb, var(--text-inverse) 5%, transparent);
  border-radius: var(--radius-lg);
  margin-bottom: 16px;
}

.user-details {
  flex: 1;
}

.mobile-user-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Mobile Menu Button - Hidden on desktop, shown on mobile */
.mobile-menu-btn {
  display: none;
  padding: 8px !important;
  min-width: 44px !important;
  height: 44px !important;
}

.hamburger {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 24px;
  height: 24px;
}

.hamburger span {
  display: block;
  width: 20px;
  height: 2px;
  background: var(--text-primary);
  border-radius: var(--radius-xs);
  transition: all 0.3s ease;
}

/* Mobile Actions Container - Hidden on desktop */
.mobile-actions {
  display: none;
  align-items: center;
  gap: 8px;
}

@media (max-width: 1023px) {
  .desktop-nav,
  .auth-section {
    display: none;
  }

  .mobile-actions {
    display: flex;
  }

  .mobile-menu-btn {
    display: flex !important;
  }
}

@media (max-width: 639px) {
  .header-container {
    padding: 0 16px;
  }

  .mobile-menu {
    width: 100%;
  }
}
</style>
